import * as React from 'react';
import { Link } from 'react-router-dom';

import * as Edge from '../../../core';
import TrainingBanner from '../trainingBanner';
import Summary from '../summary';
import SkillScores from '../skillScores';
import TrainingPlan from '../trainingPlan';
import MainRow from '../../../layouts/baseLayouts/mainRow';
import { withLoadDataDefaultConfig } from '../../../components/loadData';
import errorLoadingWrapperHOC from '../../../components/errorLoadingWrapper/errorLoadingWrapperHOC';
import { StatsService } from '../../../services/statsService';
import { PlanService } from '../../../services/planService';

import { ReactComponent as CheckmarkIcon } from '../../../assets/images/icons/Checkmark.svg';
import { ReactComponent as LockIcon } from '../../../assets/images/icons/Lock.svg';
import { ReactComponent as GlassesIcon } from '../../../assets/images/icons/glasses.svg';
import { ReactComponent as NotebookIcon } from '../../../assets/images/icons/Notebook.svg';
import { ReactComponent as OrderIcon } from '../../../assets/images/icons/Order.svg';
import { ReactComponent as StatsIcon } from '../../../assets/images/icons/Stats.svg';
import { ReactComponent as ShieldIcon } from '../../../assets/images/icons/Shield.svg';
import { ReactComponent as TargetIcon } from '../../../assets/images/icons/Target.svg';
import EATGraphic from '../../../assets/images/EAT_graphic.svg';

import './index.scss';

export interface AthleteDashboardProps {
	evaluationResults: Edge.Models.EvaluationResultSummary[];
	skillScores: Edge.Models.SkillScores;
	trainingPlanResponse: Edge.Models.TrainingPlanResult;
}

interface AthleteDashboardState {}

export class AthleteDashboard extends React.PureComponent<AthleteDashboardProps, AthleteDashboardState> {
	public render(): JSX.Element {
		const { evaluationResults, skillScores, trainingPlanResponse } = this.props;
		const viewingSelf = true; // AthleteDashboard will always display TrainingPlan for self.

		return evaluationResults.length > 0 ? (
			<>
				<MainRow>
					<TrainingBanner />
				</MainRow>
				<MainRow>
					<Summary results={evaluationResults} alignmentResult={skillScores.alignment} />
					<div>
						<SkillScores results={skillScores} />
						<TrainingPlan trainingPlanResponse={trainingPlanResponse} viewingSelf={viewingSelf} />
					</div>
				</MainRow>
			</>
		) : (
			<div className="athlete_dashboard welcome">
				<div className="welcome_container">
					<header className="welcome_header">
						<h2>Welcome to Vizual Edge</h2>
						<h3>Your Training Journey Starts Here.</h3>
						<p>
							The Edge System uses a continuous cycle of evaluation, analysis, and training to help you
							build stronger visual and cognitive skills over time.
						</p>
					</header>

					<section className="eat_section">
						<div className="eat_layout">
							<div className="eat_step eat_step_evaluate">
								<div className="eat_step_icon">
									<NotebookIcon />
								</div>
								<h4>Evaluate</h4>
								<p>Take the Edge Test to measure your visual and cognitive skills.</p>
							</div>

							<div className="eat_graphic">
								<img src={EATGraphic} alt="Evaluate, Analyze, Train continuous improvement cycle" />
							</div>

							<div className="eat_step eat_step_analyze">
								<div className="eat_step_icon">
									<StatsIcon />
								</div>
								<h4>Analyze</h4>
								<p>
									Receive a detailed report with insights into your strengths, weaknesses, and
									opportunities.
								</p>
							</div>

							<div className="eat_step eat_step_train">
								<div className="eat_step_icon">
									<TargetIcon />
								</div>
								<h4>Train</h4>
								<p>
									Follow a personalized training plan that targets your key skills and builds
									consistency.
								</p>
							</div>
						</div>
					</section>

					<div className="ready_divider">
						<span>Ready to Begin?</span>
					</div>

					<section className="cta_cards">
						<div className="cta_card">
							<div className="cta_card_header">
								<div className="cta_card_graphic">
									<GlassesIcon />
								</div>
								<div className="cta_card_header_text">
									<h4>I have my Vizual Edge 3D Glasses</h4>
									<p className="cta_card_subtext">Unlock the full Edge System.</p>
								</div>
							</div>
							<ul>
								<li>
									<span className="check">
										<CheckmarkIcon />
									</span>
									Edge Test &amp; Report
								</li>
								<li>
									<span className="check">
										<CheckmarkIcon />
									</span>
									Personalized Plan (My Plan)
								</li>
								<li>
									<span className="check">
										<CheckmarkIcon />
									</span>
									Open Gym Mode
								</li>
								<li>
									<span className="check">
										<CheckmarkIcon />
									</span>
									Game Day Mode
								</li>
							</ul>
							<Link to="/training/evaluation" className="cta_btn btn_small begin_edge_test_btn">
								<span>Begin Edge Test</span>
								<span className="cta_chevron" />
							</Link>
						</div>

						<div className="cta_card">
							<div className="cta_card_header">
								<div className="cta_card_graphic shipping">
									<OrderIcon />
								</div>
								<div className="cta_card_header_text">
									<h4>My 3D glasses are on the way</h4>
									<p className="cta_card_subtext">Start exploring Vizual Edge today.</p>
								</div>
							</div>
							<ul>
								<li>
									<span className="check">
										<CheckmarkIcon />
									</span>
									Select Open Gym drills
								</li>
								<li>
									<span className="check">
										<CheckmarkIcon />
									</span>
									Learn how the platform works
								</li>
								<li>
									<span className="check">
										<CheckmarkIcon />
									</span>
									Be ready to begin your Edge Test when your glasses arrive
								</li>
								<li className="cta_card_item_locked">
									<span className="lock">
										<LockIcon />
									</span>
									Full Edge System Access (Edge Test, My Plan &amp; Game Day)
								</li>
							</ul>
							<Link to="/my-account/buy" className="cta_card_order_link">
								Need to order 3D glasses?
							</Link>
							<Link to="/training/openGym" className="cta_btn btn_bright btn_small explore_training_btn">
								<span>Explore Open Gym</span>
								<span className="cta_chevron" />
							</Link>
						</div>
					</section>

					<div className="trust_banner">
						<div className="trust_icon">
							<ShieldIcon />
						</div>
						<p>
							Trusted by athletes, patients, coaches and clinics worldwide to build better visual
							performance.
						</p>
					</div>
				</div>
			</div>
		);
	}
}

export default withLoadDataDefaultConfig(
	errorLoadingWrapperHOC(AthleteDashboard),
	(props) => {},
	async () => {
		const [evaluationResults, skillScores, trainingPlanResponse] = await Promise.all([
			StatsService.getMyEvaluationResults(),
			StatsService.getSkillScores(),
			PlanService.getMyTrainingPlan(),
		]);
		return { evaluationResults, skillScores, trainingPlanResponse };
	}
);
